import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import './Home.css';
import Modal from 'react-modal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import KeepMyAssetLogo from '../Image/Logo.png'
import { Helmet } from 'react-helmet';
import { PublicHome } from './PublicSite';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  FaTrash,
  FaPlus,
  FaInfoCircle,
  FaShieldAlt,
  FaUserCheck,
  FaLock,
  FaCheckCircle,
  FaBell,
  FaCloudUploadAlt,
  FaUsers,
  FaUniversity,
  FaFileInvoiceDollar,
  FaLandmark,
  FaExclamationTriangle,
  FaHeart,
  FaUserShield,
  FaClock,
  FaGlobe,
  FaDatabase,
  FaHeartBroken,
  FaEye,
  FaDownload,
  FaSpinner,
  FaCheckDouble,
  FaUserFriends,
  FaHandHoldingHeart,
  FaFamilyProtect,
  FaEdit
} from 'react-icons/fa';

Modal.setAppElement('#root');

const Home = ({ user, onGoogleSignIn , onNavigateApplicationOverview, dashboardRefreshKey }) => {
  const [nominees, setNominees] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [isSigninModalOpen, setSigninModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    nomineeEmail: '',
    nomineePhone: '',
    policyDocument: null,
    providerName: ''
  });
  const [secureEnabled, setSecureEnabled] = useState(false);
  const [secureInfoOpen, setSecureInfoOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nomineeId: null,
    policyId: null,
    nomineeEmail: '',
    nomineePhone: '',
    policyDocument: null,
    providerName: ''
  });
  const [errors, setErrors] = useState({ 
    nomineePhone: '', 
    policyDocument: '',
    nomineeEmail: '',
    providerName: ''
  });
  const [editErrors, setEditErrors] = useState({ 
    nomineePhone: '', 
    policyDocument: '',
    nomineeEmail: '',
    providerName: ''
  });
  const [isAddressModalOpen, setAddressModalOpen] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressForm, setAddressForm] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: ''
  });
  const [addressErrors, setAddressErrors] = useState({
    addressLine1: '',
    city: '',
    state: '',
    postalCode: ''
  });
  const [hasAddress, setHasAddress] = useState(false);
  const [secureEnableRequested, setSecureEnableRequested] = useState(false);


  const fetchNominees = useCallback(async () => {
    
    if (user?.id) {
      const token = localStorage.getItem('token');
      setLoading(true);
      try {
       const response = await axios.get(
  `${process.env.REACT_APP_HOST_SERVER}/api/nominees/find/by/UserId?userId=${user.id}`,
  {
    timeout: 600000,
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  }
);
        setNominees(response.data.data || []);
      } catch (error) {
        console.error('Error fetching nominee data:', error);
        setNominees([]);
        if (error.code !== 'ECONNABORTED') {
          showNotification('Failed to load nominees. Please refresh the page.', 'error');
        }
      } finally {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchNominees();
  }, [fetchNominees]);

  // If redirected after login, open Add Nominee modal once phone is on file
  useEffect(() => {
    const shouldOpen = sessionStorage.getItem('openAddNominee');
    if (!user || shouldOpen !== '1') return;

    const candidates = [user.contact, user.phone, user.phoneNumber, user.mobile, user.mobileNumber]
      .filter(Boolean)
      .map(String);
    const hasPhone = candidates.some((value) => {
      const digits = value.replace(/\D/g, '').slice(-10);
      return /^\d{10}$/.test(digits);
    });

    if (hasPhone) {
      sessionStorage.removeItem('openAddNominee');
      setModalOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (!secureInfoOpen) return;
    const closeInfo = (event) => {
      if (event.target.closest('.secure-mode-info-wrap') || event.target.closest('.secure-mode-info-popover')) {
        return;
      }
      setSecureInfoOpen(false);
    };
    const timerId = window.setTimeout(() => {
      document.addEventListener('click', closeInfo);
      document.addEventListener('touchstart', closeInfo);
    }, 0);
    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('click', closeInfo);
      document.removeEventListener('touchstart', closeInfo);
    };
  }, [secureInfoOpen]);

  // Inline Google button rendering in Sign-in modal
  const googleBtnContainerRef = useRef(null);
  const googleBtnRenderedRef = useRef(false);
  useEffect(() => {
    if (!isSigninModalOpen) return;
    try {
      if (window.google?.accounts?.id && googleBtnContainerRef.current && !googleBtnRenderedRef.current) {
        window.google.accounts.id.renderButton(googleBtnContainerRef.current, {
          theme: 'filled_blue',
          size: 'large',
          shape: 'pill',
          width: 280,
          text: 'signin_with'
        });
        googleBtnRenderedRef.current = true;
      }
    } catch {}
  }, [isSigninModalOpen]);

  const handleRemove = async (nomineeId, policyId) => {
    const confirmMessage = `Are you sure you want to remove this nominee?\n\nThis action cannot be undone and the nominee will no longer have access to this policy information.`;
    const token= localStorage.getItem('token');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/${nomineeId}/policy/${policyId}`,
        { timeout: 10000, headers: {
      'Authorization': `Bearer ${token}`,
    } }
      );
      setNominees(nominees.filter(n => n.nomineeId !== nomineeId));
      showNotification('Nominee removed successfully! 🗑️', 'success');
    } catch (error) {
      console.error('Error removing nominee:', error);
      showNotification('Failed to remove nominee. Please try again.', 'error');
    }
  };

  const handleEditNominee = (nominee) => {
    setEditFormData({
      nomineeId: nominee.nomineeId,
      policyId: nominee.policyId,
      nomineeEmail: nominee.nomineeEmail,
      nomineePhone: nominee.nomineePhone,
      providerName: nominee.providerName,
      policyDocument: null // File input starts empty
    });
    setEditErrors({
      nomineePhone: '', 
      policyDocument: '',
      nomineeEmail: '',
      providerName: ''
    });
    setEditModalOpen(true);
  };

  const isValidDocumentUrl = (url) => {
    const trimmed = String(url || '').trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return false;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return true;
    }
    return false;
  };

  const handleDocumentClick = (event, url) => {
    event.stopPropagation();
    if (!isValidDocumentUrl(url)) {
      event.preventDefault();
      showNotification('Document is not available yet. Please upload a file first.', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 0.5rem;">
        ${type === 'success' ? '✅' : '❌'}
        <span>${message}</span>
      </div>
    `;

    const bgColor = type === 'success' ? '#10b981' : '#ef4444';
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 9999;
      background: ${bgColor}; color: white; padding: 1rem 1.5rem;
      border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.2);
      font-weight: 500; font-family: 'Inter', sans-serif;
      animation: slideInRight 0.3s ease-out, fadeOut 0.3s ease-in 3.7s;
      max-width: 400px; word-wrap: break-word;
    `;

    // Add animation keyframes if not already added
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email address is required.';
    if (!emailRegex.test(email)) return 'Please enter a valid email address (e.g., john@example.com).';
    if (email.length > 100) return 'Email address must be less than 100 characters.';
    if (email.includes('..')) return 'Email address cannot contain consecutive dots.';
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required.';
    if (!/^[0-9]{10}$/.test(phone)) return 'Phone number must be exactly 10 digits.';
    if (phone.startsWith('0')) return 'Phone number should not start with 0.';
    if (phone.startsWith('1')) return 'Phone number should not start with 1.';
    if (/^(.)\1{9}$/.test(phone)) return 'Phone number cannot have all same digits.';
    return '';
  };

  const validateFile = (file, isRequired = true) => {
    if (!file && isRequired) return 'Please select a document.';
    if (!file) return ''; // Optional file for edit

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = 2 * 1024 * 1024; // 2MB limit
    const minSize = 1024; // 1KB minimum

    if (!allowedTypes.includes(file.type)) {
      return 'Only PDF, JPG, and PNG files are allowed.';
    }

    if (file.size > maxSize) {
      return `File size must be less than 2MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`;
    }

    if (file.size < minSize) {
      return 'File seems too small. Please upload a valid document.';
    }

    return '';
  };

  const validateProviderName = (name) => {
    if (!name) return 'Provider name is required.';
    const trimmedName = name.trim();
    if (trimmedName.length < 2) return 'Provider name must be at least 2 characters.';
    if (trimmedName.length > 50) return 'Provider name must be less than 50 characters.';
    if (!/^[a-zA-Z0-9\s&.-]+$/.test(trimmedName)) {
      return 'Provider name can only contain letters, numbers, spaces, &, ., and -.';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const [secureUpdating, setSecureUpdating] = useState(false);
  const handleSecureToggle = async (event) => {
    const newValue = event.target.checked;
    const prev = secureEnabled;

    if (newValue && !hasAddress) {
      setSecureEnableRequested(true);
      setSecureEnabled(false);
      setAddressModalOpen(true);
      return;
    }

    setSecureEnabled(newValue);
    const token = localStorage.getItem('token');
    try {
      setSecureUpdating(true);
      const resp = await axios.put(
        `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode`,
        { isSecure: newValue, userId: user?.id },
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
      );
      const raw =
        (resp.data && (resp.data.isSecure ?? resp.data.secure)) ??
        (resp.data?.data && (resp.data.data.isSecure ?? resp.data.data.secure));
      const confirmed =
        raw === true || raw === false ? raw : newValue;
      setSecureEnabled(Boolean(confirmed));
    } catch (err) {
      console.error("Error updating secure flag:", err);
      setSecureEnabled(prev);
    } finally {
      setSecureUpdating(false);
    }
  };

  // Fetch secure mode state on login and dashboard navigation
  useEffect(() => {
    const fetchSecure = async () => {
      if (!user?.id) return;
      const token = localStorage.getItem('token');
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode?userId=${user.id}`,
          { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
        );
        // Accept boolean from multiple possible shapes and string booleans
        const raw =
          (resp.data && (resp.data.isSecure ?? resp.data.secure)) ??
          (resp.data?.data && (resp.data.data.isSecure ?? resp.data.data.secure));
        const value = raw === true || raw === false ? raw : String(raw).toLowerCase() === 'true';
        setSecureEnabled(Boolean(value));
      } catch (e) {
        console.error('Failed to fetch secure mode:', e);
      }
    };
    fetchSecure();
  }, [user, dashboardRefreshKey]);

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const fetchUserAddress = useCallback(async () => {
    if (!user?.id) {
      setHasAddress(false);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const resp = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/address/${user.id}` , {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) {
        setHasAddress(false);
        return;
      }
      const json = await resp.json();
      const a = json?.data || json;
      const exists = Boolean(
        a &&
        String(a.addressLine1 || '').trim() &&
        String(a.city || '').trim() &&
        String(a.state || '').trim() &&
        (String(a.postalCode || a.pincode || '').trim())
      );
      setHasAddress(exists);
    } catch (e) {
      console.error('Failed to fetch address:', e);
      setHasAddress(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserAddress();
  }, [fetchUserAddress]);

  const validateAddress = (data) => {
    const errs = { addressLine1: '', city: '', state: '', postalCode: '' };
    if (!String(data.addressLine1 || '').trim()) errs.addressLine1 = 'Address Line 1 is required.';
    if (!String(data.city || '').trim()) errs.city = 'City is required.';
    if (!String(data.state || '').trim()) errs.state = 'State is required.';
    const pin = String(data.postalCode || '').trim();
    if (!pin) {
      errs.postalCode = 'PIN/Postal code is required.';
    } else if (!/^\d{6}$/.test(pin)) {
      errs.postalCode = 'PIN code must be 6 digits.';
    }
    return errs;
  };

  const handleAddressInputChange = (field, value) => {
    setAddressForm(prev => ({ ...prev, [field]: field === 'postalCode' ? value.replace(/\D/g, '').slice(0, 6) : value }));
    if (addressErrors[field]) {
      setAddressErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // (Removed state/district/pincode dropdown logic per request)

  const closeAddressModal = () => {
    setAddressModalOpen(false);
    setAddressForm({ addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '' });
    setAddressErrors({ addressLine1: '', city: '', state: '', postalCode: '' });
    setSecureEnableRequested(false);
  };

  const handleSaveAddress = async () => {
    const errs = validateAddress(addressForm);
    setAddressErrors(errs);
    const hasAnyError = Object.values(errs).some(Boolean);
    if (hasAnyError) {
      showNotification('Please fix the errors in the address form.', 'error');
      return;
    }

    try {
      setAddressSaving(true);
      const token = localStorage.getItem('token');
      const resp = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          userId: user?.id,
          addressLine1: addressForm.addressLine1.trim(),
          addressLine2: String(addressForm.addressLine2 || '').trim(),
          city: addressForm.city.trim(),
          state: addressForm.state.trim(),
          postalCode: addressForm.postalCode.trim(),
        }),
      });
      if (!resp.ok) {
        throw new Error('Failed to save address');
      }
      await fetchUserAddress();
      showNotification('Address saved successfully.', 'success');
      // If user intended to enable secure mode, enable it now after successful address save
      if (secureEnableRequested) {
        try {
          setSecureUpdating(true);
          await axios.put(
            `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode`,
            { isSecure: true, userId: user?.id },
            { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
          );
          setSecureEnabled(true);
          setSecureEnableRequested(false);
        } catch (e) {
          console.error('Failed to enable secure mode after address save:', e);
          showNotification('Secure Mode could not be enabled. Please try toggling again.', 'error');
        } finally {
          setSecureUpdating(false);
        }
      }
      closeAddressModal();
    } catch (e) {
      console.error('Failed to save address:', e);
      showNotification('Could not save address. Please try again.', 'error');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddNominee = async (e) => {
    e.preventDefault();
    const { nomineeEmail, nomineePhone, policyDocument, providerName } = formData;

    // Validate all fields with enhanced validation
    const emailError = validateEmail(nomineeEmail?.trim());
    const phoneError = validatePhone(nomineePhone?.trim());
    const docError = validateFile(policyDocument);
    const providerError = validateProviderName(providerName?.trim());
    const token= localStorage.getItem('token');

    setErrors({
      nomineeEmail: emailError,
      nomineePhone: phoneError,
      policyDocument: docError,
      providerName: providerError
    });

    // Check if any errors exist
    if (emailError || phoneError || docError || providerError) {
      showNotification('Please fix the errors in the form before submitting.', 'error');
      return;
    }

    setSubmitLoading(true);
    const payload = new FormData();
    payload.append('nomineeEmail', nomineeEmail.trim().toLowerCase());
    payload.append('nomineePhone', nomineePhone.trim());
    payload.append('providerName', providerName.trim());
    payload.append('userId', user.id);
    payload.append('policyDocument', policyDocument);

    try {
      await axios.post(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/create/via/file`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
          timeout: 30000, // 30 second timeout
        }
      );

      showNotification('🎉 Nominee added successfully! They will be notified when needed.', 'success');
      closeModal();
      fetchNominees();
    } catch (error) {
      console.error('Error adding nominee:', error);
      let errorMessage = 'Failed to add nominee. Please try again.';

      if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please upload a file smaller than 2MB.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check all fields and try again.';
      } else if (error.response?.status === 409) {
        errorMessage = 'This nominee already exists for this policy.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. Please check your connection and try again.';
      }

      showNotification(errorMessage, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleUpdateNominee = async (e) => {
    e.preventDefault();
    const { nomineeId, policyId, nomineeEmail, nomineePhone, policyDocument, providerName } = editFormData;

    // Validate all fields (file is optional for edit)
    const emailError = validateEmail(nomineeEmail?.trim());
    const phoneError = validatePhone(nomineePhone?.trim());
    const docError = validateFile(policyDocument, false); // File is optional for edit
    const providerError = validateProviderName(providerName?.trim());

    setEditErrors({
      nomineeEmail: emailError,
      nomineePhone: phoneError,
      policyDocument: docError,
      providerName: providerError
    });

    // Check if any errors exist
    if (emailError || phoneError || docError || providerError) {
      showNotification('Please fix the errors in the form before submitting.', 'error');
      return;
    }

    setEditLoading(true);
    const payload = new FormData();
    payload.append('nomineeEmail', nomineeEmail.trim().toLowerCase());
    payload.append('nomineePhone', nomineePhone.trim());
    payload.append('providerName', providerName.trim());
    payload.append('userId', user.id);
    if (policyDocument) {
      payload.append('file', policyDocument);
    }

    try {
      const token =localStorage.getItem('token');
      const response = await axios.patch(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/${nomineeId}/policy/${policyId}/update`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
          timeout: 30000,
        }
      );

      if (response.data.successful) {
        showNotification('🎉 Nominee updated successfully!', 'success');
        closeEditModal();
        fetchNominees();
      }
    } catch (error) {
      console.error('Error updating nominee:', error);
      let errorMessage = 'Failed to update nominee. Please try again.';

      if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please upload a file smaller than 2MB.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check all fields and try again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Nominee not found. Please refresh the page and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. Please check your connection and try again.';
      }

      showNotification(errorMessage, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormData({ 
      nomineeEmail: '', 
      nomineePhone: '', 
      providerName: '', 
      policyDocument: null 
    });
    setErrors({ 
      nomineePhone: '', 
      policyDocument: '',
      nomineeEmail: '',
      providerName: ''
    });
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditFormData({
      nomineeId: null,
      policyId: null,
      nomineeEmail: '',
      nomineePhone: '',
      policyDocument: null,
      providerName: ''
    });
    setEditErrors({ 
      nomineePhone: '', 
      policyDocument: '',
      nomineeEmail: '',
      providerName: ''
    });
  };

  // Handle "Check If You're a Nominee" button click
  const handleCheckNomineeClick = () => {
    if (onGoogleSignIn) {
      onGoogleSignIn(); // Trigger Google Sign-In
    }
  };

  const handleAddNewClick = () => {
    try {
      sessionStorage.setItem('openAddNominee', '1');
      if (!user) {
        // Open the global header Sign-In modal
        try {
          window.dispatchEvent(new Event('open-auth-modal'));
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {}
        return;
      }
      setModalOpen(true);
    } catch (e) {
      if (!user && onGoogleSignIn) onGoogleSignIn();
    }
  };

  // Dashboard metrics
  if (!user) {
    return <PublicHome />;
  }

  // Dashboard metrics
  const documentCount = nominees.length;
  const nomineeCount = nominees.length;
  const onboardingSteps = [
    { label: 'Create account', done: Boolean(user) },
    { label: 'Add first document', done: documentCount > 0 },
    { label: 'Add nominee', done: nomineeCount > 0 },
    { label: 'Verify email', done: Boolean(user?.email) },
    { label: 'Complete profile', done: hasAddress || secureEnabled },
  ];
  const onboardingProgress = Math.round((onboardingSteps.filter((s) => s.done).length / onboardingSteps.length) * 100);
  const missingNominees = nomineeCount === 0;
  const missingDocuments = documentCount === 0;

  // Authenticated user dashboard
  return (
    <>
    <Helmet>
        <title>Home Page - keepmyasset</title>
        <meta name="description" content="This is the home page description for SEO." />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Home Page - keepmyasset" />
        <meta property="og:description" content="This is the home page description for SEO." />
        <meta property="og:url" content="https://www.keepmyasset.com" />
        <meta property="og:type" content="website" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Home Page - keepmyasset" />
        <meta name="twitter:description" content="This is the home page description for SEO." />
  </Helmet>
      <div className="home-layout">
      <section className="dashboard-summary">
        <div className="dashboard-summary-header">
          <div>
            <h2>Family Protection Dashboard</h2>
            <p>Track your assets, nominees, and protection status at a glance.</p>
          </div>
        </div>

        <div className="dashboard-stats">
          <div className="dashboard-stat">
            <FaFileInvoiceDollar className="dashboard-stat-icon" />
            <div>
              <strong>{documentCount}</strong>
              <span>Documents</span>
            </div>
          </div>
          <div className="dashboard-stat">
            <FaUsers className="dashboard-stat-icon" />
            <div>
              <strong>{nomineeCount}</strong>
              <span>Nominees</span>
            </div>
          </div>
          <div className="dashboard-stat is-active">
            <FaBell className="dashboard-stat-icon" />
            <div>
              <strong>On</strong>
              <span>Notifications</span>
            </div>
          </div>
          <div className={`dashboard-stat ${secureEnabled ? 'is-active' : ''}`}>
            <FaShieldAlt className="dashboard-stat-icon" />
            <div>
              <strong>{secureEnabled ? 'Active' : 'Basic'}</strong>
              <span>Security</span>
            </div>
          </div>
        </div>

        {nomineeCount === 0 && !loading && (
          <div className="onboarding-panel">
            <div className="onboarding-header">
              <h3>Get started — protect your family in 5 steps</h3>
              <span>{onboardingProgress}% complete</span>
            </div>
            <div className="onboarding-bar">
              <div className="onboarding-fill" style={{ width: `${onboardingProgress}%` }} />
            </div>
            <div className="onboarding-steps">
              {onboardingSteps.map((step) => (
                <div className={`onboarding-step ${step.done ? 'done' : ''}`} key={step.label}>
                  {step.done ? <FaCheckCircle /> : <FaClock />}
                  {step.label}
                </div>
              ))}
            </div>
            <button className="nominee-btn add" type="button" onClick={() => setModalOpen(true)}>
              <FaPlus /> Add Your First Nominee
            </button>
          </div>
        )}

        {(missingNominees || missingDocuments) && !loading && (
          <div className="dashboard-alerts">
            {missingDocuments && (
              <div className="dashboard-alert">
                <FaExclamationTriangle />
                <span>Missing documents — add your first policy or asset record.</span>
              </div>
            )}
            {missingNominees && (
              <div className="dashboard-alert">
                <FaExclamationTriangle />
                <span>Missing nominees — add someone your family can rely on.</span>
              </div>
            )}
          </div>
        )}

        {nomineeCount > 0 && nomineeCount < 2 && !loading && (
          <div className="dashboard-alert">
            <FaExclamationTriangle />
            <span>Consider adding more nominees to strengthen your family&apos;s protection.</span>
          </div>
        )}
      </section>

      <section className="user-info-section">
        <div className="section-header">
          <div className="section-header-top">
            <h3>
              <FaUsers className="section-header-icon" />
              <span>Your Protected Nominees</span>
            </h3>
            <button
              className="add-icon-btn"
              onClick={() => setModalOpen(true)}
              disabled={loading}
              aria-label="Add nominee"
            >
              <FaPlus className="add-icon" />
            </button>
          </div>

          <div className={`secure-mode-bar ${secureEnabled ? 'is-on' : ''} ${secureInfoOpen ? 'info-open' : ''}`}>
            <div className="secure-mode-leading">
              <div className="secure-mode-icon-wrap" aria-hidden="true">
                <FaLock />
              </div>
              <div className="secure-mode-label">
                <span className="secure-mode-title">Secure Mode</span>
                <span className="secure-mode-status">{secureEnabled ? 'On' : 'Off'}</span>
                <div className="secure-mode-info-wrap">
                  <button
                    type="button"
                    className="secure-mode-info-btn"
                    aria-label="Secure mode information"
                    aria-describedby="secure-mode-info-text"
                    aria-expanded={secureInfoOpen}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setSecureInfoOpen((open) => !open);
                    }}
                  >
                    <InfoOutlinedIcon className="secure-mode-info" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              className={`secure-mode-toggle ${secureEnabled ? 'is-on' : ''}`}
              onClick={() => handleSecureToggle({ target: { checked: !secureEnabled } })}
              disabled={secureUpdating}
              aria-pressed={secureEnabled}
              aria-label="Toggle secure mode"
            >
              <span className="secure-mode-toggle-thumb" />
            </button>
            <div className="secure-mode-info-popover" id="secure-mode-info-text" role="tooltip">
              When Secure Mode is ON, nominee checks require manual verification before any
              notification is sent.
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center', 
            padding: '4rem 2rem',
            background: 'linear-gradient(145deg, #ffff 0%, #f8fafc 100%)',
            borderRadius: '1.5rem',
            marginTop: '2rem'
          }}>
            <FaSpinner style={{
              fontSize: '3rem',
              color: '#2563eb',
              animation: 'spin 1s linear infinite',
              marginBottom: '1.5rem'
            }} />
            <h4 style={{
              color: '#334155',
              marginBottom: '0.5rem',
              fontSize: '1.25rem',
              fontWeight: '600'
            }}>Loading Your Nominees...</h4>
            <p style={{color: '#64748b', fontSize: '1rem'}}>
              Fetching your protected family members
            </p>
          </div>
        ) : nominees.length > 0 ? (
          <div className="nominee-cards-container">
            {nominees.map((nominee, index) => (
              <div className="nominee-card" key={nominee.nomineeId} style={{
                animationDelay: `${index * 0.1}s`
              }}>
                <div className="card-actions">
                  <FaEdit
                    className="edit-icon"
                    title="Edit Nominee"
                    size={35}
                    onClick={() => handleEditNominee(nominee)}
                  />
                  <FaTrash
                    className="delete-icon"
                    onClick={() => handleRemove(nominee.nomineeId, nominee.policyId)}
                    size={35}
                    title="Remove Nominee"
                  />
                </div>
                <div className="nominee-info">
                  <div className="nominee-field">
                    <span className="label">
                      <FaCheckCircle style={{color: '#10b981', marginRight: '0.5rem'}} />
                      Email:
                    </span>
                    <span className="value">{nominee.nomineeEmail}</span>
                  </div>
                  <div className="nominee-field">
                    <span className="label">
                      <FaBell style={{color: '#f59e0b', marginRight: '0.5rem'}} />
                      Phone:
                    </span>
                    <span className="value">{nominee.nomineePhone}</span>
                  </div>
                  <div className="nominee-field">
                    <span className="label">
                      <FaShieldAlt style={{color: '#2563eb', marginRight: '0.5rem'}} />
                      Provider:
                    </span>
                    <span className="value">{nominee.providerName}</span>
                  </div>
                  <div className="nominee-field document-field">
                    <span className="label">
                      <FaFileInvoiceDollar style={{color: '#8b5cf6', marginRight: '0.5rem'}} />
                      Document:
                    </span>
                    {isValidDocumentUrl(nominee.documentUrl) ? (
                      <a
                        className="document-link"
                        href={nominee.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => handleDocumentClick(event, nominee.documentUrl)}
                      >
                        <FaDownload />
                        View Document
                      </a>
                    ) : (
                      <span className="document-missing">No document uploaded</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FaUserShield className="empty-state-icon" />
            <h4>Protect Your Family's Future Today</h4>
            <p>
              Start securing your loved ones by adding your first nominee. 
              It takes less than 2 minutes and provides lifelong peace of mind for your entire family.
            </p>
            <button 
              className="nominee-btn add"
              onClick={() => setModalOpen(true)}
              style={{marginTop: '1rem'}}
            >
              <FaPlus />
              Add Your First Nominee
            </button>

            <div className="empty-state-features">
              <div className="empty-state-feature">
                <FaLock className="empty-state-feature-icon" />
                <span>Bank-Level Security</span>
              </div>
              <div className="empty-state-feature">
                <FaBell className="empty-state-feature-icon" />
                <span>Smart Notifications</span>
              </div>
              <div className="empty-state-feature">
                <FaShieldAlt className="empty-state-feature-icon" />
                <span>Privacy Protected</span>
              </div>
              <div className="empty-state-feature">
                <FaClock className="empty-state-feature-icon" />
                <span>2-Minute Setup</span>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Add Nominee Modal */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          contentLabel="Add Nominee"
          className="modal"
          overlayClassName="modal-overlay"
        >
          <h2>
            <FaUserCheck style={{color: '#2563eb'}} />
            Add New Nominee
          </h2>
          <form onSubmit={handleAddNominee} className="modal-form">
            <label>
              Nominee Email Address *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-email"
                data-tooltip-content="Enter a valid email address for secure notifications and policy updates"
              />
              <input
                type="email"
                required
                placeholder="e.g. john.doe@example.com"
                className={errors.nomineeEmail ? 'input-error' : ''}
                value={formData.nomineeEmail}
                onChange={(e) => handleInputChange('nomineeEmail', e.target.value)}
                maxLength={100}
                disabled={submitLoading}
              />
              {errors.nomineeEmail && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {errors.nomineeEmail}
                </div>
              )}
            </label>

            <label>
              Phone Number *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-phone"
                data-tooltip-content="10-digit Indian mobile number (without +91 or 0 prefix)"
              />
              <input
                type="tel"
                required
                placeholder="e.g. 9876543210"
                className={errors.nomineePhone ? 'input-error' : ''}
                value={formData.nomineePhone}
                onChange={(e) => handleInputChange('nomineePhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                maxLength={10}
                disabled={submitLoading}
              />
              {errors.nomineePhone && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {errors.nomineePhone}
                </div>
              )}
            </label>

            <label>
               Provider Name *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-provider"
                data-tooltip-content="Name of your insurance company (e.g., LIC, HDFC Life, ICICI Prudential)"
              />
              <input
                type="text"
                required
                placeholder="e.g. LIC, HDFC Life, ICICI Prudential"
                className={errors.providerName ? 'input-error' : ''}
                value={formData.providerName}
                onChange={(e) => handleInputChange('providerName', e.target.value)}
                maxLength={50}
                disabled={submitLoading}
              />
              {errors.providerName && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {errors.providerName}
                </div>
              )}
            </label>

            <label>
              Document *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-doc"
                data-tooltip-content="Upload PDF, JPG, or PNG file (maximum 2MB). Ensure document is clear and readable."
              />
              <label className="custom-file-upload">
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/jpg"
                  required
                  onChange={(e) => handleInputChange('policyDocument', e.target.files[0])}
                  disabled={submitLoading}
                />
                <FaCloudUploadAlt className="upload-icon" />
                {formData.policyDocument ? formData.policyDocument.name : 'Choose Document'}
              </label>
              {formData.policyDocument && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe'
                }}>
                  <FaFileInvoiceDollar style={{marginRight: '0.5rem'}} />
                  {formData.policyDocument.name} 
                  <span style={{color: '#64748b', marginLeft: '0.5rem'}}>
                    ({(formData.policyDocument.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
              {errors.policyDocument && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {errors.policyDocument}
                </div>
              )}
            </label>

            <div className="modal-buttons">
              <button 
                type="submit" 
                className="add-btn"
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Adding Nominee...
                  </>
                ) : (
                  <>
                    <FaCheckDouble />
                    Add Nominee
                  </>
                )}
              </button>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={closeModal}
                disabled={submitLoading}
              >
                Cancel
              </button>
            </div>
          </form>

          <ReactTooltip id="tooltip-email" place="top" />
          <ReactTooltip id="tooltip-phone" place="top" />
          <ReactTooltip id="tooltip-doc" place="top" />
          <ReactTooltip id="tooltip-provider" place="top" />
        </Modal>

        {/* Edit Nominee Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onRequestClose={closeEditModal}
          contentLabel="Edit Nominee"
          className="modal"
          overlayClassName="modal-overlay"
        >
          <h2>
            <FaEdit style={{color: '#2563eb'}} />
            Edit Nominee
          </h2>
          <form onSubmit={handleUpdateNominee} className="modal-form">
            <label>
              Nominee Email Address *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-edit-email"
                data-tooltip-content="Enter a valid email address for secure notifications and policy updates"
              />
              <input
                type="email"
                required
                placeholder="e.g. john.doe@example.com"
                className={editErrors.nomineeEmail ? 'input-error' : ''}
                value={editFormData.nomineeEmail}
                onChange={(e) => handleEditInputChange('nomineeEmail', e.target.value)}
                maxLength={100}
                disabled={editLoading}
              />
              {editErrors.nomineeEmail && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.nomineeEmail}
                </div>
              )}
            </label>

            <label>
              Phone Number *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-edit-phone"
                data-tooltip-content="10-digit Indian mobile number (without +91 or 0 prefix)"
              />
              <input
                type="tel"
                required
                placeholder="e.g. 9876543210"
                className={editErrors.nomineePhone ? 'input-error' : ''}
                value={editFormData.nomineePhone}
                onChange={(e) => handleEditInputChange('nomineePhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                maxLength={10}
                disabled={editLoading}
              />
              {editErrors.nomineePhone && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.nomineePhone}
                </div>
              )}
            </label>

            <label>
              Provider Name *
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-edit-provider"
                data-tooltip-content="Name of your insurance company (e.g., LIC, HDFC Life, ICICI Prudential)"
              />
              <input
                type="text"
                required
                placeholder="e.g. LIC, HDFC Life, ICICI Prudential"
                className={editErrors.providerName ? 'input-error' : ''}
                value={editFormData.providerName}
                onChange={(e) => handleEditInputChange('providerName', e.target.value)}
                maxLength={50}
                disabled={editLoading}
              />
              {editErrors.providerName && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.providerName}
                </div>
              )}
            </label>

            <label>
              Document (Optional - leave empty to keep current document)
              <FaInfoCircle
                className="tooltip-icon"
                data-tooltip-id="tooltip-edit-doc"
                data-tooltip-content="Upload PDF, JPG, or PNG file (maximum 2MB) to replace the current document. Leave empty to keep existing document."
              />
              <label className="custom-file-upload">
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleEditInputChange('policyDocument', e.target.files[0])}
                  disabled={editLoading}
                />
                <FaCloudUploadAlt className="upload-icon" />
                {editFormData.policyDocument ? editFormData.policyDocument.name : 'Choose New Document (Optional)'}
              </label>
              {editFormData.policyDocument && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe'
                }}>
                  <FaFileInvoiceDollar style={{marginRight: '0.5rem'}} />
                  {editFormData.policyDocument.name} 
                  <span style={{color: '#64748b', marginLeft: '0.5rem'}}>
                    ({(editFormData.policyDocument.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
              {editErrors.policyDocument && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.policyDocument}
                </div>
              )}
            </label>

            <div className="modal-buttons">
              <button 
                type="submit" 
                className="add-btn"
                disabled={editLoading}
              >
                {editLoading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Updating Nominee...
                  </>
                ) : (
                  <>
                    <FaCheckDouble />
                    Update Nominee
                  </>
                )}
              </button>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
            </div>
          </form>

          <ReactTooltip id="tooltip-edit-email" place="top" />
          <ReactTooltip id="tooltip-edit-phone" place="top" />
          <ReactTooltip id="tooltip-edit-doc" place="top" />
          <ReactTooltip id="tooltip-edit-provider" place="top" />
        </Modal>

        {/* Add Address Modal (on enabling Secure Mode) */}
        <Modal
          isOpen={isAddressModalOpen}
          onRequestClose={closeAddressModal}
          contentLabel="Add Address"
          className="modal"
          overlayClassName="modal-overlay"
        >
          <h2>
            Add Address
          </h2>
          <div className="modal-form">
            <label>
              Address Line 1 *
              <input
                type="text"
                placeholder="House/Flat, Street"
                value={addressForm.addressLine1}
                onChange={(e) => handleAddressInputChange('addressLine1', e.target.value)}
                className={addressErrors.addressLine1 ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={120}
              />
              {addressErrors.addressLine1 && (
                <div className="error-message">{addressErrors.addressLine1}</div>
              )}
            </label>

            <label>
              Address Line 2 (Optional)
              <input
                type="text"
                placeholder="Area, Landmark"
                value={addressForm.addressLine2}
                onChange={(e) => handleAddressInputChange('addressLine2', e.target.value)}
                disabled={addressSaving}
                maxLength={120}
              />
            </label>

            <label>
              City *
              <input
                type="text"
                placeholder="City"
                value={addressForm.city}
                onChange={(e) => handleAddressInputChange('city', e.target.value)}
                className={addressErrors.city ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={60}
              />
              {addressErrors.city && (
                <div className="error-message">{addressErrors.city}</div>
              )}
            </label>

            <label>
              State *
              <input
                type="text"
                placeholder="State"
                value={addressForm.state}
                onChange={(e) => handleAddressInputChange('state', e.target.value)}
                className={addressErrors.state ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={60}
              />
              {addressErrors.state && (
                <div className="error-message">{addressErrors.state}</div>
              )}
            </label>

            <label>
              PIN Code *
              <input
                type="tel"
                placeholder="6-digit PIN"
                value={addressForm.postalCode}
                onChange={(e) => handleAddressInputChange('postalCode', e.target.value)}
                className={addressErrors.postalCode ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={6}
              />
              {addressErrors.postalCode && (
                <div className="error-message">{addressErrors.postalCode}</div>
              )}
            </label>

            <div className="modal-buttons">
              <button
                type="button"
                className="add-btn"
                onClick={handleSaveAddress}
                disabled={addressSaving}
              >
                {addressSaving ? 'Saving...' : 'Save Address'}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={closeAddressModal}
                disabled={addressSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </section>
    </div>
    </>
    
  );
};

export default Home;
